// Payment plan / settlement breakdown math for the payment schedule slider.
// Extracted from PaymentPlanPayment so the installment-floor edge cases are unit-testable.
//
// The floor is a caller-supplied argument, not a constant: it is the portfolio's minimum for the
// selected payment method (see paymentPlanApplicableMinimum.js), so it varies by portfolio and by
// method.

// Used only while the BE floors haven't loaded. Deliberately permissive — it is the lowest floor
// the BE configures, and the BE re-validates on submit, so a stale fallback surfaces as a server
// error rather than silently blocking a settlement the agent is allowed to make.
export const DEFAULT_MIN_INSTALLMENT_AMOUNT = 5;

// FALLBACK ceiling on a settlement discount, used only when the server-provided manager
// max hasn't loaded (or failed to load). The authoritative per-loan value arrives via
// /payment_plan/info (max_discount_percent.manager), sourced from lox_api's
// PaymentPlanDiscountService::$successfulPaymentToSettlementDiscount, where every tier
// currently caps managers at 90%.
export const MAX_SETTLEMENT_DISCOUNT_PERCENT = 90;

// Floors a dollar amount to whole cents. The epsilon (a hundredth of a cent) absorbs
// floating-point noise so a value one ulp below an exact cent doesn't lose a cent.
const floorToCents = (value) => Math.floor(value * 100 + 1e-6) / 100;

/**
 * Resolves the settlement discount ceiling: the server-provided manager max percent when
 * it's a valid positive number, otherwise the MAX_SETTLEMENT_DISCOUNT_PERCENT fallback.
 * @param {number|string|null} managerMaxPercent - max_discount_percent.manager from /payment_plan/info.
 * @returns {number} - Ceiling percent.
 */
export const getSettlementDiscountCeilingPercent = (managerMaxPercent = null) => {
  const parsed = parseFloat(managerMaxPercent);
  return parsed > 0 ? parsed : MAX_SETTLEMENT_DISCOUNT_PERCENT;
};

/**
 * Returns the number of payments that can be made in two years for a pay frequency.
 * @param {string} frequency - Pay frequency label ('Weekly', 'Bi-Weekly', 'Semi-Monthly', 'Monthly').
 * @returns {number} - Number of payments, or 0 for an unknown frequency.
 */
export const installmentsInTwoYears = (frequency) => {
  const numberYear = 2;
  switch (frequency) {
    case 'Weekly':
      return 52 * numberYear - 1;
    case 'Bi-Weekly':
      return 26 * numberYear - 1;
    case 'Semi-Monthly':
      return 24 * numberYear - 1;
    case 'Monthly':
      return 12 * numberYear - 1;
    default:
      return 0;
  }
};

/**
 * Returns the largest settlement discount that keeps the discounted balance at or above the
 * applicable minimum, also respecting the discount ceiling (the manager max when provided).
 * For 'amount' the ceiling is derived from the same percent, matching how the API computes
 * max_discount_amount from max_discount_percent.
 * @param {number|string} outstandingTotal - The pre-discount outstanding balance.
 * @param {string} discountType - 'percent' or 'amount'.
 * @param {number|string|null} managerMaxPercent - max_discount_percent.manager from /payment_plan/info.
 * @param {number} minInstallmentAmount - The applicable per-payment minimum for the selected method.
 * @returns {number} - Maximum discount (a percent or a dollar amount), never negative.
 */
export const getMaxSettlementDiscount = (outstandingTotal, discountType, managerMaxPercent = null, minInstallmentAmount = DEFAULT_MIN_INSTALLMENT_AMOUNT) => {
  const total = parseFloat(outstandingTotal);
  if (!total || total <= 0) {
    return 0;
  }
  const ceilingPercent = getSettlementDiscountCeilingPercent(managerMaxPercent);
  if (discountType === 'percent') {
    return Math.max(0, Math.min(ceilingPercent, ((total - minInstallmentAmount) / total) * 100));
  }
  return Math.max(0, Math.min(total * (ceilingPercent / 100), total - minInstallmentAmount));
};

/**
 * Whether the balance floor (rather than the discount ceiling) is what limits the discount for
 * this balance — i.e. whether the floor message, not the manager-max message, explains the cap.
 * @param {number|string} outstandingTotal - The pre-discount outstanding balance.
 * @param {string} discountType - 'percent' or 'amount'.
 * @param {number|string|null} managerMaxPercent - max_discount_percent.manager from /payment_plan/info.
 * @param {number} minInstallmentAmount - The applicable per-payment minimum for the selected method.
 * @returns {boolean}
 */
export const isBalanceFloorBinding = (outstandingTotal, discountType, managerMaxPercent = null, minInstallmentAmount = DEFAULT_MIN_INSTALLMENT_AMOUNT) => {
  const total = parseFloat(outstandingTotal);
  if (!total || total <= 0) {
    return false;
  }
  const ceilingPercent = getSettlementDiscountCeilingPercent(managerMaxPercent);
  const ceiling = discountType === 'percent' ? ceilingPercent : total * (ceilingPercent / 100);
  return getMaxSettlementDiscount(outstandingTotal, discountType, managerMaxPercent, minInstallmentAmount) < ceiling;
};

/**
 * The cap to apply to the discount form fields: getMaxSettlementDiscount rounded DOWN
 * to two decimals so the value fits the inputs' step="0.01" while still keeping the
 * discounted balance at or above the applicable minimum.
 * @param {number|string} outstandingTotal - The pre-discount outstanding balance.
 * @param {string} discountType - 'percent' or 'amount'.
 * @param {number|string|null} managerMaxPercent - max_discount_percent.manager from /payment_plan/info.
 * @param {number} minInstallmentAmount - The applicable per-payment minimum for the selected method.
 * @returns {number} - Maximum field value, never negative.
 */
export const getMaxSettlementDiscountFieldValue = (outstandingTotal, discountType, managerMaxPercent = null, minInstallmentAmount = DEFAULT_MIN_INSTALLMENT_AMOUNT) =>
  floorToCents(getMaxSettlementDiscount(outstandingTotal, discountType, managerMaxPercent, minInstallmentAmount));

/**
 * Calculates the minimum payment amount and the number of installments to offer for a
 * payment plan. The discount is clamped to getMaxSettlementDiscount so the discounted
 * balance can never drop below the applicable minimum (which previously produced a
 * zero-installment division and an Infinity min payment).
 * @param {object} params
 * @param {number|string} params.outstandingTotal - The pre-discount outstanding balance.
 * @param {string} params.frequency - Pay frequency label ('Weekly', 'Bi-Weekly', 'Semi-Monthly', 'Monthly').
 * @param {string} params.discountType - 'percent' or 'amount'.
 * @param {number|string} params.discountValue - The discount the agent entered.
 * @param {number|string|null} params.managerMaxPercent - max_discount_percent.manager from /payment_plan/info.
 * @param {number} params.minInstallmentAmount - The applicable per-payment minimum for the selected method.
 * @returns {{minPayment: number, installmentCount: number}}
 */
export const calculatePaymentPlanBreakdown = ({ outstandingTotal, frequency, discountType, discountValue, managerMaxPercent = null, minInstallmentAmount = DEFAULT_MIN_INSTALLMENT_AMOUNT }) => {
  const total = parseFloat(outstandingTotal);
  const defaultInstallments = installmentsInTwoYears(frequency);
  if (!total || total <= 0 || defaultInstallments === 0) {
    return { minPayment: minInstallmentAmount, installmentCount: defaultInstallments };
  }

  const maxDiscount = getMaxSettlementDiscount(total, discountType, managerMaxPercent, minInstallmentAmount);
  const appliedDiscount = Math.min(Math.max(parseFloat(discountValue) || 0, 0), maxDiscount);
  const balance = discountType === 'percent'
    ? total - total * (appliedDiscount / 100)
    : total - appliedDiscount;

  let installmentCount = defaultInstallments;
  // Floor (not round) so the min payment can never come out a cent above the
  // installment the API will calculate for the same inputs.
  let minPayment = floorToCents(balance / installmentCount);
  if (minPayment < minInstallmentAmount) {
    // Fewer installments so each one meets the floor. The clamp above guarantees
    // balance >= minInstallmentAmount unless the loan itself is under the floor,
    // in which case the only viable plan is a single full-balance installment.
    installmentCount = Math.max(1, Math.floor(balance / minInstallmentAmount));
    minPayment = floorToCents(balance / installmentCount);
  }

  return { minPayment, installmentCount };
};
